import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { BrandService } from '../../../services/brand-config.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';

@Component({
  selector: 'app-camera-consent',
  standalone: true,
  imports: [
    MatButton,
    MatDialogModule,
    MatCheckboxModule,
    FormsModule,
    A11yModule,
    CommonModule,
    TranslatePipe,
  ],
  templateUrl: './camera-consent.component.html',
  styleUrl: './camera-consent.component.scss',
})
export class CameraConsentComponent implements AfterViewInit, OnDestroy {
  requiredConsent = false;
  optionalDataImprovement = false;
  optionalDataSharing = false;
  optionalTracking = false;
  showScrollHint = false;

  @ViewChild('scrollContent', { read: ElementRef })
  scrollContent?: ElementRef<HTMLElement>;

  private resizeObserver?: ResizeObserver;

  constructor(
    private dialogRef: MatDialogRef<CameraConsentComponent>,
    public brandService: BrandService,
    private translationService: TranslationService
  ) {}

  ngAfterViewInit() {
    setTimeout(() => this.updateScrollHint(), 0);
    const el = this.scrollContent?.nativeElement;
    if (el && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updateScrollHint());
      this.resizeObserver.observe(el);
    }
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  onScroll() {
    this.updateScrollHint();
  }

  scrollToBottom() {
    const el = this.scrollContent?.nativeElement;
    el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }

  private updateScrollHint() {
    const el = this.scrollContent?.nativeElement;
    if (!el) {
      this.showScrollHint = false;
      return;
    }
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    this.showScrollHint = el.scrollHeight > el.clientHeight + 8 && !atBottom;
  }

  get isMarkable(): boolean {
    return this.brandService.isMarkableHost;
  }

  get brandKey(): string {
    return this.isMarkable ? 'markable' : 'peritale';
  }

  get canSubmit(): boolean {
    return this.requiredConsent;
  }

  submitConsent() {
    const consentData = {
      dataImprovement: this.optionalDataImprovement,
      dataSharing: this.optionalDataSharing,
      tracking: this.optionalTracking,
      // The consent sentences exactly as shown, keyed by stable consent_key, so
      // the server can hash + version them (text_version on consent_events).
      texts: this.renderedConsentTexts(),
    };

    // Analytics / marketing cookies (GTM, Google Ads, FullStory, HEAP) stay off until she
    // ticks the tracking clause here; Google Consent Mode defaults to denied before this.
    const apply = (window as any).applyTrackingConsent;
    if (typeof apply === 'function') {
      apply(this.optionalTracking ? 'granted' : 'denied');
    }

    this.dialogRef.close(consentData);
  }

  /** The consent label strings as displayed, keyed by stable consent_key. */
  private renderedConsentTexts(): { [key: string]: string } {
    const b = this.brandKey;
    const brand = this.brandService.config.brandName;
    const t = (k: string, p?: any) => this.translationService.translate(k, p);
    return {
      'camera.required': t(`cameraConsent.${b}.requiredConsentLabel`),
      'camera.dataImprovement': t(
        `cameraConsent.${b}.optionalDataImprovementLabel`,
        { brand }
      ),
      'camera.dataSharing': t(`cameraConsent.${b}.optionalDataSharingLabel`),
      'camera.tracking': t(`cameraConsent.${b}.optionalTrackingLabel`),
    };
  }
}
